import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './CommercialEntityForm.css';
import { useClassificationOptions, ClassificationField, marketAddOptions, marketRowFor, marketRowText, heldAddOptions } from '../../../shared/OrganisationClassification';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { thumbFileUrl } from '../../../shared/mediaFileUrl';
import { COUNTRIES, DEFAULT_COUNTRY } from '../../../shared/countries';
import { addressParts, addressWarnings, partsDroppedFor, capitaliseWords } from '../../../shared/addressFormat';
import { RegionSelect, AddressFieldWarning, SuburbInput } from '../../../shared/AddressFields';
import NumberInput from '../../../shared/NumberInput';
import { getTenantHomeCurrency } from '../../../shared/numberFormat';
import { carrierChoices, servicesForCarrier, carrierForService } from '../../../shared/shippingCarriage';
import { BUSINESS_NUMBER_TYPES, businessNumberTypeForCountry, spaceAbnInput } from '../../../shared/businessNumber';

// Strips the query string/fragment from a URL (e.g. Google Shopping's
// "?srsltid=..." click-tracking param) and any now-dangling trailing slash
// — mirrors websitePreviewService.js's cleanUrl on the backend, applied
// on blur so a pasted tracking link tidies up immediately rather than
// waiting for Save.
function cleanUrl(input) {
  const trimmed = String(input || '').trim();
  if (!trimmed) return '';
  return trimmed.split('#')[0].split('?')[0].replace(/\/$/, '');
}

// Generic representational glyphs (not the platforms' trademarked logo
// assets) — just enough for at-a-glance identification next to each Social
// field, matching the app's plain monochrome icon style elsewhere.
function LinkedInIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#5a6b85" aria-hidden="true">
      <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM.5 8.98h4V23h-4V8.98zM8.5 8.98h3.84v1.92h.05c.53-1 1.85-2.06 3.8-2.06 4.06 0 4.81 2.67 4.81 6.14V23h-4v-6.42c0-1.53-.03-3.5-2.13-3.5-2.14 0-2.47 1.67-2.47 3.39V23h-4V8.98z" />
    </svg>
  );
}
function FacebookIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#5a6b85" aria-hidden="true">
      <path d="M22 12a10 10 0 10-11.5 9.87v-6.98H7.9V12h2.6V9.8c0-2.56 1.52-3.98 3.86-3.98 1.12 0 2.3.2 2.3.2v2.5h-1.3c-1.28 0-1.68.8-1.68 1.62V12h2.86l-.46 2.89h-2.4v6.98A10 10 0 0022 12z" />
    </svg>
  );
}
function InstagramIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5a6b85" strokeWidth="2" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="17.5" cy="6.5" r="1" fill="#5a6b85" stroke="none" />
    </svg>
  );
}

// Australia Post's Suburbs for an Australian Postcode, as it's typed (#158).
async function fetchPostcodeCheck(query) {
  const res = await axios.get('/api/postcode-check', { params: query });
  return res.data.result;
}

function mapAddresses(addresses) {
  return (addresses || []).map((a) => ({
    address_type_id: a.address_type_id ?? '',
    line1: a.line1 || '',
    line2: a.line2 || '',
    district: a.district || '',
    suburb: a.suburb || '',
    state: a.state || '',
    postcode: a.postcode || '',
    country: a.country || DEFAULT_COUNTRY,
    is_primary: !!a.is_primary,
  }));
}

function mapEmails(emails) {
  return (emails || []).map((e) => ({ email_type_id: e.email_type_id ?? '', email: e.email || '' }));
}

function mapPhones(phones) {
  return (phones || []).map((p) => ({ phone_type_id: p.phone_type_id ?? '', phone_number: p.phone_number || '' }));
}

// Display Name defaults to "First Last" for an Individual but is editable —
// once the person filling the form has typed into Name directly, first/last
// changes stop overwriting it.
//
// creationContext says which list screen "Add" was opened from — it picks
// the Relationship Type/Stage the new entity is seeded with
// (vis_relationship_lifecycle.md §3), on top of Type/Shape above:
// 'lead' (anywhere on Prospects & Customers but the Customers tab,
// Prospect @ Lead), 'customer' (Customers tab, Customer @ Active — a Stage
// requiring commercial terms, so Tier and Customer Price Group are both
// required), or 'free' (Market Intel's All Entities — shows its
// own Relationship Type/Stage pickers below and lets any Type be created
// directly, including a Manufacturer/Distributor).
function CommercialEntityForm({ initial, onSave, onCancel, loading, creationContext }) {
  const overrides = initial?.overrides || {};
  const [type, setType] = useState(initial?.type || '');
  const [firstName, setFirstName] = useState(initial?.first_name || '');
  const [lastName, setLastName] = useState(initial?.last_name || '');
  const [jobTitle, setJobTitle] = useState(initial?.job_title || '');
  const [name, setName] = useState(initial?.name || '');
  // Whether Display Name should keep auto-following First/Last Name edits.
  // Off only when an existing record's Display Name already diverges from
  // "First Last" — i.e. it was deliberately customized at some point — so
  // that customization is never silently overwritten. A new record (no
  // `initial`) always starts on, same as an existing one whose Display
  // Name still matches "First Last" exactly (the common case): editing
  // First/Last there should keep updating Display Name, not freeze it the
  // moment the record is opened for editing.
  const nameManuallyEdited = useRef(
    Boolean(initial) && initial.type === 'individual'
      && String(initial.name || '').trim() !== `${initial.first_name || ''} ${initial.last_name || ''}`.trim()
  );
  const [legalEntityTypeId, setLegalEntityTypeId] = useState(initial?.legal_entity_type_id ?? '');
  const [sourceId, setSourceId] = useState(initial?.source_id ?? '');
  // Organisations only — the Markets, Operators and Channels it's
  // classified under, one primary each.
  const [markets, setMarkets] = useState(initial?.markets || []);
  const [operators, setOperators] = useState(initial?.operators || []);
  const [channels, setChannels] = useState(initial?.channels || []);
  const { markets: marketOptions, segments: segmentOptions, channels: channelOptions, operators: operatorOptions } = useClassificationOptions();
  // Business Number (issue #286): an unchosen type follows the primary
  // address's country.
  const [businessNumber, setBusinessNumber] = useState(initial?.business_number || '');
  const [businessNumberType, setBusinessNumberType] = useState(initial?.business_number_type || '');
  const [sync, setSync] = useState(initial?.sync ?? false);
  const [offerPackingList, setOfferPackingList] = useState(initial?.offer_packing_list ?? true);
  const [addresses, setAddresses] = useState(mapAddresses(initial?.addresses));
  const [emails, setEmails] = useState(mapEmails(initial?.emails));
  const [phones, setPhones] = useState(mapPhones(initial?.phones));
  // Website/About Us (URL-lookup feature) — Organisation-only. Logo/Socials
  // apply to both types, so they're read from `initial` regardless of type.
  const [locale, setLocale] = useState(initial?.locale || '');
  const [website, setWebsite] = useState(initial?.website || '');
  const [aboutUs, setAboutUs] = useState(initial?.about_us || '');
  const [logo, setLogo] = useState(initial?.logo || null);
  const [linkedinUrl, setLinkedinUrl] = useState(initial?.linkedin_url || '');
  const [facebookUrl, setFacebookUrl] = useState(initial?.facebook_url || '');
  const [instagramUrl, setInstagramUrl] = useState(initial?.instagram_url || '');
  const [fetching, setFetching] = useState(false);
  const [fetchWarnings, setFetchWarnings] = useState({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [sources, setSources] = useState([]);
  const [legalEntityTypes, setLegalEntityTypes] = useState([]);
  // The tenant's Relationship Types (with their own Stages nested) — the
  // pickers for creationContext 'free', and every context's Entity Cards
  // grid column.
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [relationshipTypeId, setRelationshipTypeId] = useState('');
  const [relationshipStageId, setRelationshipStageId] = useState('');
  // Picking Relationship Type = Supplier also asks for its trading Currency
  // — same field as the Suppliers screen's own Add form (SupplierForm.js),
  // defaulted to the home currency. No exchange rate: rates are reference
  // rates per currency, shared system-wide (issue #303).
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currencyCode, setCurrencyCode] = useState(() => getTenantHomeCurrency());
  const [addressTypes, setAddressTypes] = useState([]);
  const [phoneTypes, setPhoneTypes] = useState([]);
  const [emailTypes, setEmailTypes] = useState([]);
  // A Tier is a set of default commercial terms only — it never decides
  // Prospect vs Customer or moves a Stage (vis_relationship_lifecycle.md §6).
  const [tierId, setTierId] = useState(initial?.tier_id ?? '');
  // Add Customer only: the Customer Price Group the new record is priced
  // at, pre-filled from the chosen Tier's default. Left on the Tier's
  // default it stores nothing and keeps following the Tier; any other group
  // is assigned across all Products (see commercialTerms.js).
  const [customerPriceGroupId, setCustomerPriceGroupId] = useState('');
  const [customerPriceGroups, setCustomerPriceGroups] = useState([]);
  // Carrier picked ahead of the Service (issue #281) — changing it clears
  // the Service, which clears the default Options (effect below).
  const [carrierId, setCarrierId] = useState(initial?.carrier_id != null ? String(initial.carrier_id) : '');
  const [shippingServiceId, setShippingServiceId] = useState(initial?.shipping_service_id ?? '');
  // Default Option selections for shippingServiceId above (cascades onto a
  // new Quote/Sales Order — see QuotePage.js's own applyCustomer). Always
  // scoped to shippingServiceId, so it's cleared whenever that changes —
  // same rule the Quote/Sales Order screens' own ShippingOptionsPicker
  // enforces for a document's selections.
  const [defaultOptionIds, setDefaultOptionIds] = useState(
    (initial?.default_options || []).map((o) => o.shipping_option.id)
  );
  const [optionsForService, setOptionsForService] = useState([]);
  const [paymentTermId, setPaymentTermId] = useState(overrides.payment_term_id ?? '');
  const [creditLimit, setCreditLimit] = useState(overrides.credit_limit ?? '');
  const [taxHandlingTypeId, setTaxHandlingTypeId] = useState(overrides.tax_handling_type_id != null ? String(overrides.tax_handling_type_id) : '');
  const [customerTiers, setCustomerTiers] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [taxHandlingTypes, setTaxHandlingTypes] = useState([]);
  const [shippingServices, setShippingServices] = useState([]);
  // New addresses default Country to the business's own Country (Company
  // Settings), not a hardcoded value — falls back to DEFAULT_COUNTRY only
  // if Company Settings has no row yet.
  const [defaultCountry, setDefaultCountry] = useState(DEFAULT_COUNTRY);
  const primaryCountry = (addresses.find((a) => a.is_primary) || addresses[0])?.country || defaultCountry;
  const effectiveNumberType = businessNumberType || businessNumberTypeForCountry(primaryCountry);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/sources', { params: { status: 'active' } })
      .then((res) => setSources(res.data.data || []))
      .catch(() => setSources([]));
    axios.get('/api/legal-entity-types', { params: { status: 'active' } })
      .then((res) => setLegalEntityTypes(res.data.data || []))
      .catch(() => setLegalEntityTypes([]));
    axios.get('/api/address-types', { params: { status: 'active' } })
      .then((res) => setAddressTypes(res.data.data || []))
      .catch(() => setAddressTypes([]));
    axios.get('/api/phone-types', { params: { status: 'active' } })
      .then((res) => setPhoneTypes(res.data.data || []))
      .catch(() => setPhoneTypes([]));
    axios.get('/api/email-types', { params: { status: 'active' } })
      .then((res) => setEmailTypes(res.data.data || []))
      .catch(() => setEmailTypes([]));
    axios.get('/api/customer-tiers', { params: { status: 'active' } })
      .then((res) => setCustomerTiers(res.data.data || []))
      .catch(() => setCustomerTiers([]));
    axios.get('/api/customer-price-groups', { params: { status: 'active' } })
      .then((res) => setCustomerPriceGroups((res.data.data || []).filter((g) => g.is_active)))
      .catch(() => setCustomerPriceGroups([]));
    axios.get('/api/payment-terms', { params: { status: 'active' } })
      .then((res) => setPaymentTerms(res.data.data || []))
      .catch(() => setPaymentTerms([]));
    axios.get('/api/tax-handling-types', { params: { status: 'active' } })
      .then((res) => setTaxHandlingTypes(res.data.data || []))
      .catch(() => setTaxHandlingTypes([]));
    axios.get('/api/shipping-services', { params: { status: 'all' } })
      .then((res) => setShippingServices(res.data.data || []))
      .catch(() => setShippingServices([]));
    axios.get('/api/company-settings')
      .then((res) => { if (res.data.data?.country) setDefaultCountry(res.data.data.country); })
      .catch(() => {});
    axios.get('/api/relationship-types', { params: { status: 'active' } })
      .then((res) => setRelationshipTypes(res.data.data || []))
      .catch(() => setRelationshipTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picking a Relationship Type defaults Stage to its first (lowest
  // sort_order) Stage — same default the backend applies when none is sent
  // — and clears Stage entirely for a Type with none (Manufacturer/
  // Distributor by default).
  const selectedRelationshipType = relationshipTypes.find((rt) => String(rt.id) === String(relationshipTypeId));
  // Sections follow the Entity Cards grid's column for the Relationship
  // Type being created (issue #249) — on All Entities, only the
  // always-shown sections until a Type is picked. Customer Terms always
  // shows on the Customers tab, where a Tier and Customer Price Group are
  // required.
  const creatingType = creationContext === 'free'
    ? selectedRelationshipType
    : relationshipTypes.find((rt) => rt.system_key === (creationContext === 'customer' ? 'customer' : 'prospect'));
  const formCards = new Set(creatingType?.card_keys || []);
  const showsSection = (key) => formCards.has(key) || (key === 'customer_terms' && creationContext === 'customer');
  // Adding straight into a Stage that can be quoted (the Customers tab, or
  // All Entities' free pick) — a new Prospect adds its price groups later,
  // from the record's own page.
  const showPriceGroupField = showsSection('customer_terms') && creationContext !== 'lead';

  // A new Tier pre-fills its default Customer Price Group, unless a group
  // has already been picked by hand.
  const handleTierChange = (nextTierId) => {
    const previousDefault = customerTiers.find((t) => String(t.id) === String(tierId))?.default_customer_price_group_id;
    const nextDefault = customerTiers.find((t) => String(t.id) === String(nextTierId))?.default_customer_price_group_id;
    setTierId(nextTierId);
    if (customerPriceGroupId === '' || String(customerPriceGroupId) === String(previousDefault ?? '')) {
      setCustomerPriceGroupId(nextDefault != null ? String(nextDefault) : '');
    }
  };
  useEffect(() => {
    setRelationshipStageId(selectedRelationshipType?.stages?.[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationshipTypeId]);

  const isAddingSupplier = creationContext === 'free' && selectedRelationshipType?.system_key === 'supplier';
  useEffect(() => {
    if (!isAddingSupplier || currencyOptions.length) return;
    axios.get('/api/currencies', { params: { limit: 100, status: 'active' } })
      .then((res) => setCurrencyOptions(res.data.data || []))
      .catch(() => setCurrencyOptions([]));
  }, [isAddingSupplier, currencyOptions.length]);

  useEffect(() => {
    if (!shippingServiceId) { setOptionsForService([]); return; }
    axios.get('/api/shipping-options', { params: { shipping_service_id: shippingServiceId, status: 'active' } })
      .then((res) => setOptionsForService(res.data.data || []))
      .catch(() => setOptionsForService([]));
  }, [shippingServiceId]);

  // Drops any default Option selections that no longer belong to
  // shippingServiceId — never fires on first mount (prevServiceIdRef
  // starts equal to the current value), only on a genuine later change,
  // same guard ShippingOptionsPicker.js uses for the same reason.
  const prevShippingServiceIdRef = useRef(shippingServiceId);
  useEffect(() => {
    const prev = prevShippingServiceIdRef.current;
    prevShippingServiceIdRef.current = shippingServiceId;
    if (prev === shippingServiceId) return;
    setDefaultOptionIds([]);
  }, [shippingServiceId]);

  const tierOptions = customerTiers.filter(
    (t) => t.is_active || String(t.id) === String(tierId)
  );
  const selectedTier = initial?.tier;
  if (selectedTier && !tierOptions.some((t) => String(t.id) === String(selectedTier.id))) {
    tierOptions.push(selectedTier);
  }
  // The live default each "(Tier default)" option resolves to right now —
  // purely a hint for the person filling the form; plays no part in what
  // gets submitted.
  const activeTier = tierOptions.find((t) => String(t.id) === String(tierId));

  const paymentTermOptions = paymentTerms.filter(
    (pt) => pt.is_active || String(pt.id) === String(paymentTermId)
  );
  const selectedPaymentTerm = initial?.payment_term;
  if (selectedPaymentTerm && !paymentTermOptions.some((pt) => String(pt.id) === String(selectedPaymentTerm.id))) {
    paymentTermOptions.push(selectedPaymentTerm);
  }

  const carrierOptions = carrierChoices(shippingServices, carrierId);
  const shippingServiceOptions = servicesForCarrier(shippingServices, carrierId, shippingServiceId);

  const handleFirstNameChange = (value) => {
    setFirstName(value);
    if (!nameManuallyEdited.current) setName(`${value} ${lastName}`.trim());
  };
  const handleLastNameChange = (value) => {
    setLastName(value);
    if (!nameManuallyEdited.current) setName(`${firstName} ${value}`.trim());
  };
  const handleNameChange = (value) => {
    nameManuallyEdited.current = true;
    setName(value);
  };

  // Reads whatever the site itself publishes for link previews (About Us +
  // Logo, plus any social-profile links) — always best-effort, and always
  // overwrites whatever's currently in those fields without confirming,
  // since Fetch is already a deliberate click.
  const handleFetch = async () => {
    if (!website.trim() || fetching) return;
    const cleaned = cleanUrl(website);
    setWebsite(cleaned);
    setFetching(true);
    setFetchWarnings({});
    try {
      const res = await axios.post('/api/commercial-entities/fetch-website-info', { url: cleaned });
      const data = res.data.data || {};
      setAboutUs(data.about_us || '');
      setLinkedinUrl(data.linkedin_url || '');
      setFacebookUrl(data.facebook_url || '');
      setInstagramUrl(data.instagram_url || '');
      if (data.logo) setLogo(data.logo);
      setFetchWarnings(res.data.warnings || {});
    } catch (err) {
      setFetchWarnings({ general: err.response?.data?.message || "Couldn't reach that site" });
    }
    setFetching(false);
  };

  // Manual fallback for a site whose auto-fetch can't find or reach a
  // usable logo (blocks bots, no icon/og:image published, etc) — same
  // upload endpoint the detail page's own Upload control uses, just held
  // in local form state here since the record may not exist yet.
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/api/commercial-entities/logo', formData);
      setLogo(res.data.data.logo);
      setFetchWarnings((prev) => ({ ...prev, logo: undefined }));
    } catch (err) {
      setFetchWarnings((prev) => ({ ...prev, logo: err.response?.data?.message || 'Could not upload logo' }));
    }
    setUploadingLogo(false);
  };

  const sourceOptions = sources.filter(
    (s) => s.is_active || String(s.id) === String(sourceId)
  );
  const selectedSource = initial?.source;
  if (selectedSource && !sourceOptions.some((s) => String(s.id) === String(selectedSource.id))) {
    sourceOptions.push(selectedSource);
  }

  const legalEntityTypeOptions = legalEntityTypes.filter(
    (lt) => lt.is_active || String(lt.id) === String(legalEntityTypeId)
  );
  const selectedLegalEntityType = initial?.legal_entity_type;
  if (selectedLegalEntityType && !legalEntityTypeOptions.some((lt) => String(lt.id) === String(selectedLegalEntityType.id))) {
    legalEntityTypeOptions.push(selectedLegalEntityType);
  }

  const addressTypeOptionsFor = (addressTypeId) => addressTypes.filter(
    (at) => at.is_active || String(at.id) === String(addressTypeId)
  );

  const phoneTypeOptionsFor = (phoneTypeId) => phoneTypes.filter(
    (pt) => pt.is_active || String(pt.id) === String(phoneTypeId)
  );

  const emailTypeOptionsFor = (emailTypeId) => emailTypes.filter(
    (et) => et.is_active || String(et.id) === String(emailTypeId)
  );

  // A Country change keeps the parts both countries share and blanks the
  // ones the new Country doesn't show (#157).
  const updateAddress = (idx, field, value) => {
    const extra = field === 'country' ? partsDroppedFor(value) : {};
    setAddresses((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value, ...extra } : r)));
  };
  // An Australian Suburb, with the State that goes with a picked one (#158).
  const updateSuburb = (idx, fields) => setAddresses((rows) => rows.map((r, i) => (i === idx ? { ...r, ...fields } : r)));
  // The first address added is automatically Primary (#165) — later rows
  // stay non-Primary until deliberately set via the radio button below.
  const addAddress = () => setAddresses((rows) => [...rows, { address_type_id: '', line1: '', line2: '', district: '', suburb: '', state: '', postcode: '', country: defaultCountry, is_primary: rows.length === 0 }]);
  const removeAddress = (idx) => setAddresses((rows) => rows.filter((_, i) => i !== idx));
  const setPrimaryAddress = (idx) => setAddresses((rows) => rows.map((r, i) => ({ ...r, is_primary: i === idx })));

  const updateEmail = (idx, field, value) => {
    setEmails((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addEmail = () => setEmails((rows) => [...rows, { email_type_id: '', email: '' }]);
  const removeEmail = (idx) => setEmails((rows) => rows.filter((_, i) => i !== idx));

  const updatePhone = (idx, field, value) => {
    setPhones((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addPhone = () => setPhones((rows) => [...rows, { phone_type_id: '', phone_number: '' }]);
  const removePhone = (idx) => setPhones((rows) => rows.filter((_, i) => i !== idx));

  const validate = () => {
    if (!type) return 'Type is required.';
    if (type === 'individual' && (!firstName.trim() || !lastName.trim())) {
      return 'First Name and Last Name are required for an Individual.';
    }
    if (!name.trim()) return 'Name is required.';
    if (creationContext === 'customer' && !tierId) return 'Tier is required.';
    if (creationContext === 'customer' && !customerPriceGroupId) return 'Customer Price Group is required.';
    if (creationContext === 'free' && !relationshipTypeId) return 'Relationship Type is required.';
    if (isAddingSupplier && !currencyCode) return 'Currency is required.';
    if (emails.some((e) => !e.email.trim())) return 'Remove any Email row left blank, or fill it in.';
    if (phones.some((p) => !p.phone_number.trim())) return 'Remove any Phone row left blank, or fill it in.';
    if (creditLimit !== '' && (isNaN(Number(creditLimit)) || Number(creditLimit) < 0)) {
      return 'Credit Limit must be a non-negative number.';
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      creation_context: creationContext,
      ...(creationContext === 'free' ? {
        relationship_type_id: Number(relationshipTypeId),
        relationship_stage_id: relationshipStageId === '' ? null : Number(relationshipStageId),
      } : {}),
      ...(isAddingSupplier ? {
        currency_code: currencyCode,
      } : {}),
      type,
      name: name.trim(),
      first_name: type === 'individual' ? firstName.trim() : '',
      last_name: type === 'individual' ? lastName.trim() : '',
      job_title: type === 'individual' ? jobTitle.trim() : '',
      legal_entity_type_id: !showsSection('legal') || legalEntityTypeId === '' ? null : Number(legalEntityTypeId),
      source_id: !showsSection('classification') || sourceId === '' ? null : Number(sourceId),
      markets: type === 'organization' && showsSection('classification')
        ? markets.map((r) => ({ market_id: r.market_id, market_segment_id: r.market_segment_id, is_primary: r.is_primary }))
        : [],
      operators: type === 'organization' && showsSection('operators')
        ? operators.map((r) => ({ operator_id: r.operator_id, is_primary: r.is_primary }))
        : [],
      channels: type === 'organization' && showsSection('classification')
        ? channels.map((r) => ({ channel_id: r.channel_id, is_primary: r.is_primary }))
        : [],
      business_number: showsSection('legal') ? businessNumber.trim() : '',
      business_number_type: showsSection('legal') ? effectiveNumberType : null,
      addresses: addresses.map((a) => ({ ...a, address_type_id: a.address_type_id === '' ? null : Number(a.address_type_id) })),
      emails: emails.map((e) => ({ ...e, email_type_id: e.email_type_id === '' ? null : Number(e.email_type_id) })),
      phones: phones.map((p) => ({ ...p, phone_type_id: p.phone_type_id === '' ? null : Number(p.phone_type_id) })),
      tier_id: !showsSection('customer_terms') || tierId === '' ? null : Number(tierId),
      ...(showPriceGroupField && customerPriceGroupId !== '' ? { customer_price_group_id: Number(customerPriceGroupId) } : {}),
      carrier_id: !showsSection('preferences') || carrierId === '' ? null : Number(carrierId),
      shipping_service_id: !showsSection('preferences') || shippingServiceId === '' ? null : Number(shippingServiceId),
      default_option_ids: showsSection('preferences') ? defaultOptionIds.map(Number) : [],
      ...(showsSection('preferences') ? { offer_packing_list: offerPackingList } : {}),
      payment_term_id: !showsSection('customer_terms') || paymentTermId === '' ? null : Number(paymentTermId),
      credit_limit: !showsSection('customer_terms') || creditLimit === '' ? null : Number(creditLimit),
      tax_handling_type_id: !showsSection('customer_terms') || taxHandlingTypeId === '' ? null : Number(taxHandlingTypeId),
      locale: locale.trim(),
      website: cleanUrl(website),
      about_us: aboutUs.trim(),
      logo_media_image_id: logo?.id ?? null,
      linkedin_url: cleanUrl(linkedinUrl),
      facebook_url: cleanUrl(facebookUrl),
      instagram_url: cleanUrl(instagramUrl),
      sync,
    });
  };

  const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };
  const sectionStyle = { marginTop: 16, padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };

  // Same image slot for both types — an Organisation's logo, an
  // Individual's photo.
  const logoLabel = type === 'individual' ? 'Photo' : 'Logo';
  const logoPicker = (
    <div>
      <label>{logoLabel}</label><br />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {logo ? (
          <img src={thumbFileUrl(logo.id)} alt={logoLabel} style={{ width: 60, height: 60, objectFit: 'contain', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 4 }} />
        ) : (
          <div style={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', border: '1px dashed #c7cdd6', borderRadius: 6, fontSize: 11, color: '#999', textAlign: 'center' }}>
            No {logoLabel.toLowerCase()}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#1976d2', fontWeight: 500, cursor: uploadingLogo ? 'not-allowed' : 'pointer' }}>
            {uploadingLogo ? 'Uploading...' : logo ? 'Replace' : 'Upload'}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoUpload} disabled={uploadingLogo} style={{ display: 'none' }} />
          </label>
          {logo && (
            <button type="button" onClick={() => setLogo(null)} style={{ background: 'none', border: 'none', color: '#ff6f61', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0, textAlign: 'left' }}>
              Clear
            </button>
          )}
        </div>
      </div>
      {fetchWarnings.logo && <div style={{ color: '#b45309', fontSize: 12, marginTop: 4, maxWidth: 140 }}>{fetchWarnings.logo}</div>}
    </div>
  );

  return (
    <form className="commercial-entity-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ ...sectionStyle, marginTop: 0 }}>
      <h4 style={sectionHeaderStyle}>Identity</h4>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {creationContext === 'free' && (
          <>
            <div>
              <label>Relationship Type<RequiredMark /></label><br />
              <select
                className="styled-input"
                value={relationshipTypeId}
                onChange={(e) => setRelationshipTypeId(e.target.value)}
                style={{ minWidth: 160 }}
              >
                <option value="">Select...</option>
                {relationshipTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>{rt.name}</option>
                ))}
              </select>
            </div>
            {selectedRelationshipType?.stages?.length > 0 && (
              <div>
                <label>Stage</label><br />
                <select
                  className="styled-input"
                  value={relationshipStageId}
                  onChange={(e) => setRelationshipStageId(e.target.value)}
                  style={{ minWidth: 140 }}
                >
                  {selectedRelationshipType.stages.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            {isAddingSupplier && (
              <div>
                <label>Currency<RequiredMark /></label><br />
                <select className="styled-input" value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)} style={{ minWidth: 160 }}>
                  <option value="">Select currency</option>
                  {currencyOptions.map((c) => (
                    <option key={c.currency_code} value={c.currency_code}>{c.currency_code} - {c.currency_name}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
        <div>
          <label>Shape<RequiredMark /></label><br />
          <select className="styled-input" value={type} onChange={(e) => setType(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">Select...</option>
            <option value="organization">Organisation</option>
            <option value="individual">Individual</option>
          </select>
        </div>
        {type === 'individual' && (
          <>
            <div>
              <label>First Name<RequiredMark /></label><br />
              <input className="styled-input" value={firstName} onChange={(e) => handleFirstNameChange(e.target.value)} maxLength={100} style={{ width: 160 }} />
            </div>
            <div>
              <label>Last Name<RequiredMark /></label><br />
              <input className="styled-input" value={lastName} onChange={(e) => handleLastNameChange(e.target.value)} maxLength={100} style={{ width: 160 }} />
            </div>
            <div>
              <label>Job Title</label><br />
              <input className="styled-input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} maxLength={100} style={{ width: 160 }} />
            </div>
          </>
        )}
        <div>
          <label>Display Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            maxLength={150}
            placeholder={type === 'individual' ? 'Defaults to First Last' : 'e.g., ACME Pty Ltd'}
            style={{ width: 260 }}
          />
        </div>
        {type === 'organization' && (
          <div>
            <label>Locale</label><br />
            <input
              className="styled-input"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              maxLength={100}
              placeholder="e.g., Canberra"
              title="Used to locate the business on Google"
              style={{ width: 160 }}
            />
          </div>
        )}
      </div>
      {type === 'organization' && (
        <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label>Website</label><br />
                <input
                  className="styled-input"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  onBlur={(e) => setWebsite(cleanUrl(e.target.value))}
                  placeholder="e.g., acme.com.au"
                  style={{ width: 240 }}
                />
              </div>
              <button
                type="button"
                onClick={handleFetch}
                disabled={fetching || !website.trim()}
                style={{ padding: '7px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: fetching || !website.trim() ? 'not-allowed' : 'pointer', opacity: fetching || !website.trim() ? 0.6 : 1, fontSize: 14 }}
              >
                {fetching ? 'Fetching...' : 'Fetch'}
              </button>
            </div>
            {fetchWarnings.general && <div style={{ color: '#b45309', fontSize: 12, marginTop: 4 }}>{fetchWarnings.general}</div>}
          </div>
          {logoPicker}
        </div>
      )}
      {type === 'individual' && <div style={{ marginTop: 12 }}>{logoPicker}</div>}
      </div>

      {showsSection('classification') && (
      <div style={sectionStyle}>
      <h4 style={sectionHeaderStyle}>Classification</h4>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {type === 'organization' && (
          <div>
            <ClassificationField
              inForm
              label="Markets"
              rows={markets}
              labelOf={(r) => `${marketRowText(r)}${r.market?.is_active === false || r.market_segment?.is_active === false ? ' (deactivated)' : ''}`}
              addOptions={marketAddOptions(markets, marketOptions, segmentOptions)}
              onAdd={(value) => marketRowFor(value, marketOptions, segmentOptions)}
              onChange={setMarkets}
            />
          </div>
        )}
        {type === 'organization' && (
          <div>
            <ClassificationField
              inForm
              label="Outlets"
              rows={channels}
              labelOf={(r) => `${r.channel?.name || ''}${r.channel?.is_active === false ? ' (deactivated)' : ''}`}
              addOptions={heldAddOptions(channelOptions, channels, 'channel_id')}
              onAdd={(value) => ({ channel_id: Number(value), channel: channelOptions.find((c) => String(c.id) === value) })}
              onChange={setChannels}
            />
          </div>
        )}
        <div>
          <label>Source</label><br />
          <select className="styled-input" value={sourceId} onChange={(e) => setSourceId(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">(none)</option>
            {sourceOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{!s.is_active ? ' (inactive)' : ''}</option>
            ))}
          </select>
        </div>
      </div>
      </div>
      )}

      {type === 'organization' && showsSection('operators') && (
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Operators</h4>
        <ClassificationField
          inForm
          label="Operators"
          rows={operators}
          labelOf={(r) => `${r.operator?.name || ''}${r.operator?.is_active === false ? ' (deactivated)' : ''}`}
          addOptions={heldAddOptions(operatorOptions, operators, 'operator_id')}
          onAdd={(value) => ({ operator_id: Number(value), operator: operatorOptions.find((o) => String(o.id) === value) })}
          onChange={setOperators}
        />
      </div>
      )}

      {showsSection('legal') && (
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Legal</h4>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label>Legal Entity</label><br />
            <select className="styled-input" value={legalEntityTypeId} onChange={(e) => setLegalEntityTypeId(e.target.value)} style={{ minWidth: 150 }}>
              <option value="">(none)</option>
              {legalEntityTypeOptions.map((lt) => (
                <option key={lt.id} value={lt.id}>{lt.name}{!lt.is_active ? ' (inactive)' : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Business Number</label>{' '}
            {effectiveNumberType === 'abn' && (
            <a
              href={`https://abr.business.gov.au/Search/ResultsActive?SearchText=${encodeURIComponent(name)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#1976d2', textDecoration: 'underline' }}
            >
              Look up ABN
            </a>
            )}
            <br />
            <select className="styled-input" value={effectiveNumberType} onChange={(e) => setBusinessNumberType(e.target.value)} style={{ marginRight: 8 }} aria-label="Number Type">
              {BUSINESS_NUMBER_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <input className="styled-input" value={businessNumber} onChange={(e) => setBusinessNumber(effectiveNumberType === 'abn' ? spaceAbnInput(e.target.value) : e.target.value)} maxLength={30} style={{ width: 160 }} />
          </div>
        </div>
      </div>
      )}

      {showsSection('customer_terms') && (
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Customer Terms</h4>
        <div style={{ fontSize: 12, color: '#777', margin: '2px 0 10px' }}>
          {creationContext === 'customer'
            ? 'A Tier and a Customer Price Group are required to add a Customer.'
            : 'Optional for now — a Tier and a Customer Price Group are needed before this can be quoted.'}
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label>Tier{creationContext === 'customer' && <RequiredMark />}</label><br />
            <select
              className="styled-input"
              value={tierId}
              onChange={(e) => handleTierChange(e.target.value)}
              style={{ minWidth: 140 }}
            >
              <option value="">{creationContext === 'customer' ? 'Select...' : '(none)'}</option>
              {tierOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{!t.is_active ? ' (inactive)' : ''}</option>
              ))}
            </select>
          </div>
          {showPriceGroupField && (
            <div>
              <label>Customer Price Group{creationContext === 'customer' && <RequiredMark />}</label><br />
              <select
                className="styled-input"
                value={customerPriceGroupId}
                onChange={(e) => setCustomerPriceGroupId(e.target.value)}
                style={{ minWidth: 180 }}
              >
                <option value="">Select...</option>
                {customerPriceGroups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}{String(g.id) === String(activeTier?.default_customer_price_group_id) ? ' (Tier default)' : ''}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label>Payment Term</label><br />
            <select
              className="styled-input"
              value={paymentTermId}
              onChange={(e) => setPaymentTermId(e.target.value)}
              style={{ minWidth: 140 }}
            >
              <option value="">
                Tier default{activeTier?.payment_term?.name ? ` (${activeTier.payment_term.name})` : ''}
              </option>
              {paymentTermOptions.map((pt) => (
                <option key={pt.id} value={pt.id}>{pt.name}{!pt.is_active ? ' (inactive)' : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Credit Limit ({getTenantHomeCurrency()})</label><br />
            <NumberInput
              decimals={2}
              min={0}
              step="0.01"
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.target.value)}
              placeholder={activeTier ? `Tier default (${activeTier.credit_limit != null ? `$${activeTier.credit_limit}` : 'No credit'})` : 'Tier default'}
              className="styled-input tier-default-input"
              style={{ width: 200 }}
            />
          </div>
          <div>
            <label>Tax Handling Type</label><br />
            <select
              className="styled-input"
              value={taxHandlingTypeId}
              onChange={(e) => setTaxHandlingTypeId(e.target.value)}
              style={{ minWidth: 200 }}
            >
              <option value="">
                Tier default{activeTier?.tax_handling_type ? ` (${activeTier.tax_handling_type.name})` : ''}
              </option>
              {taxHandlingTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{!t.is_active ? ' (inactive)' : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      )}

      {showsSection('preferences') && (
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Preferences</h4>
        <div style={{ fontSize: 12, color: '#777', margin: '2px 0 10px' }}>
          Defaults that flow onto a new Quote or Sales Order for this Customer — freely overridable on either without changing this default.
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label>Carrier</label><br />
            <select
              className="styled-input"
              value={carrierId}
              onChange={(e) => { setCarrierId(e.target.value); setShippingServiceId(''); }}
              style={{ minWidth: 160 }}
            >
              <option value="">(none)</option>
              {carrierOptions.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Shipping Service</label><br />
            <select
              className="styled-input"
              value={shippingServiceId}
              onChange={(e) => { setCarrierId(carrierForService(shippingServices, e.target.value, carrierId)); setShippingServiceId(e.target.value); }}
              style={{ minWidth: 160 }}
            >
              <option value="">(none)</option>
              {shippingServiceOptions.map((sm) => (
                <option key={sm.id} value={sm.id}>{sm.description}{!sm.is_active ? ' (inactive)' : ''}</option>
              ))}
            </select>
            {optionsForService.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {optionsForService.map((option) => (
                  <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={defaultOptionIds.some((id) => String(id) === String(option.id))}
                      onChange={(e) => setDefaultOptionIds((prev) => (
                        e.target.checked
                          ? [...prev, option.id]
                          : prev.filter((id) => String(id) !== String(option.id))
                      ))}
                    />
                    {option.description}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, fontWeight: 400 }}>
          <input type="checkbox" checked={offerPackingList} onChange={(e) => setOfferPackingList(e.target.checked)} />
          Offer Packing List when their order is packed
        </label>
      </div>
      )}

      {showsSection('social_media') && (
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Social Media</h4>
        {type === 'organization' && (
          <div style={{ marginBottom: 14 }}>
            <label>About Us</label><br />
            <textarea
              className="styled-input"
              value={aboutUs}
              onChange={(e) => setAboutUs(e.target.value)}
              rows={3}
              maxLength={1000}
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }}
            />
            {fetchWarnings.about_us && <div style={{ color: '#b45309', fontSize: 12, marginTop: 2 }}>{fetchWarnings.about_us}</div>}
          </div>
        )}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><LinkedInIcon /> LinkedIn</label><br />
            <input className="styled-input" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} onBlur={(e) => setLinkedinUrl(cleanUrl(e.target.value))} placeholder="Profile URL" style={{ width: 220 }} />
          </div>
          <div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FacebookIcon /> Facebook</label><br />
            <input className="styled-input" value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} onBlur={(e) => setFacebookUrl(cleanUrl(e.target.value))} placeholder="Profile URL" style={{ width: 220 }} />
          </div>
          <div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><InstagramIcon /> Instagram</label><br />
            <input className="styled-input" value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} onBlur={(e) => setInstagramUrl(cleanUrl(e.target.value))} placeholder="Profile URL" style={{ width: 220 }} />
          </div>
        </div>
      </div>
      )}

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Addresses</h4>
        {addresses.map((row, idx) => {
          const warnings = addressWarnings(row);
          const localityParts = addressParts(row.country).filter((p) => p.key !== 'line1' && p.key !== 'line2');
          return (
          <div className="address-entry" key={idx}>
            <div className="child-row">
              <div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <input type="radio" name="primary-address" checked={row.is_primary} onChange={() => setPrimaryAddress(idx)} />
                  Primary
                </label>
              </div>
              <div>
                <label>Type</label><br />
                <select className="styled-input" value={row.address_type_id} onChange={(e) => updateAddress(idx, 'address_type_id', e.target.value)} style={{ minWidth: 130 }}>
                  <option value="">(none)</option>
                  {addressTypeOptionsFor(row.address_type_id).map((at) => (
                    <option key={at.id} value={at.id}>{at.name}{!at.is_active ? ' (inactive)' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Country<RequiredMark /></label><br />
                <select className="styled-input" value={row.country} onChange={(e) => updateAddress(idx, 'country', e.target.value)} style={{ minWidth: 150 }}>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label>Line 1</label><br />
                <input className="styled-input" value={row.line1} onChange={(e) => updateAddress(idx, 'line1', e.target.value)} onBlur={() => updateAddress(idx, 'line1', capitaliseWords(row.line1))} style={{ width: 160 }} />
                <AddressFieldWarning message={warnings.line1} />
              </div>
              <div>
                <label>Line 2</label><br />
                <input className="styled-input" value={row.line2} onChange={(e) => updateAddress(idx, 'line2', e.target.value)} onBlur={() => updateAddress(idx, 'line2', capitaliseWords(row.line2))} style={{ width: 160 }} />
              </div>
            </div>
            <div className="child-row">
              {localityParts.map((part) => (
              <div key={part.key} style={{ width: part.key === 'suburb' || part.key === 'district' ? 160 : 130 }}>
                <label>{part.label}{part.required && <RequiredMark />}</label><br />
                {part.options ? (
                  <RegionSelect country={row.country} value={row.state} options={part.options} onChange={(v) => updateAddress(idx, 'state', v)} ariaLabel={part.label} />
                ) : part.key === 'suburb' ? (
                  <SuburbInput
                    address={row}
                    fetchCheck={fetchPostcodeCheck}
                    onChange={(fields) => updateSuburb(idx, fields)}
                    inputProps={{ className: 'styled-input', 'aria-label': part.label, style: { width: '100%' } }}
                  />
                ) : (
                  <input className="styled-input" value={row[part.key]} onChange={(e) => updateAddress(idx, part.key, e.target.value)} autoComplete="off" aria-label={part.label} style={{ width: '100%' }} />
                )}
                <AddressFieldWarning message={warnings[part.key]} />
              </div>
              ))}
              <button type="button" className="remove-row-btn" onClick={() => removeAddress(idx)}>Remove</button>
            </div>
          </div>
          );
        })}
        <button type="button" className="add-row-btn" onClick={addAddress}>+ Add Address</button>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Emails</h4>
        {emails.map((row, idx) => (
          <div className="child-row" key={idx}>
            <div>
              <label>Type</label><br />
              <select className="styled-input" value={row.email_type_id} onChange={(e) => updateEmail(idx, 'email_type_id', e.target.value)} style={{ minWidth: 130 }}>
                <option value="">(none)</option>
                {emailTypeOptionsFor(row.email_type_id).map((et) => (
                  <option key={et.id} value={et.id}>{et.name}{!et.is_active ? ' (inactive)' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Email</label><br />
              <input className="styled-input" value={row.email} onChange={(e) => updateEmail(idx, 'email', e.target.value)} type="email" style={{ width: 220 }} />
            </div>
            <button type="button" className="remove-row-btn" onClick={() => removeEmail(idx)}>Remove</button>
          </div>
        ))}
        <button type="button" className="add-row-btn" onClick={addEmail}>+ Add Email</button>
        <div style={{ marginTop: 10 }}>
          <ToggleSwitch
            checked={sync}
            onChange={setSync}
            label="Email Sync"
            disabled={!sync && !emails.some((e) => e.email.trim())}
            title={!sync && !emails.some((e) => e.email.trim()) ? 'Add an email above before turning on Email Sync' : "Match and log emails for this Entity's addresses"}
          />
        </div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Phones</h4>
        {phones.map((row, idx) => (
          <div className="child-row" key={idx}>
            <div>
              <label>Phone Type</label><br />
              <select className="styled-input" value={row.phone_type_id} onChange={(e) => updatePhone(idx, 'phone_type_id', e.target.value)} style={{ minWidth: 130 }}>
                <option value="">(none)</option>
                {phoneTypeOptionsFor(row.phone_type_id).map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.name}{!pt.is_active ? ' (inactive)' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Phone</label><br />
              <input className="styled-input" value={row.phone_number} onChange={(e) => updatePhone(idx, 'phone_number', e.target.value)} style={{ width: 160 }} />
            </div>
            <button type="button" className="remove-row-btn" onClick={() => removePhone(idx)}>Remove</button>
          </div>
        ))}
        <button type="button" className="add-row-btn" onClick={addPhone}>+ Add Phone</button>
      </div>

      {error && <div style={{ color: 'red', marginTop: 12 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default CommercialEntityForm;
